import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium'
import { useRouter } from 'next/router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { getActionsColumn, ActionsColumnConfig } from './definitions/getActionsColumn'
import { getAgreementColumn, AgreementColumnConfig } from './definitions/getAgreementColumn'
import { getAssigneesColumn, AssigneesColumnConfig } from './definitions/getAssigneesColumn'
import { getBooleanColumn, BooleanColumnConfig } from './definitions/getBooleanColumn'
import { getCheckboxColumn, CheckboxColumnConfig } from './definitions/getCheckboxColumn'
import { getChipColumn, ChipSingleColumnConfig } from './definitions/getChipColumn'
import { CodeColumnConfig, getCodeColumn } from './definitions/getCodeColumn'
import { getCountryColumn, CountryColumnConfig } from './definitions/getCountryColumn'
import { getCustomEditableRenderColumn, CustomEditableColumnConfig } from './definitions/getCustomEditableColumn'
import { getCustomRenderColumn, CustomRenderColumnConfig } from './definitions/getCustomRenderColumn'
import { getDateColumn, DateColumnConfig } from './definitions/getDateColumn'
import { getLinkColumn, HyperlinkColumnConfig } from './definitions/getLinkColumns'
import { getMultiChipColumn, ChipMultipleColumnConfig } from './definitions/getMultiChipColumn'
import { getNumberColumn, NumberColumnConfig } from './definitions/getNumberColumn'
import { getPriceColumn, PriceColumnConfig } from './definitions/getPriceColumn'
import { getSupplierColumn, SupplierColumnConfig } from './definitions/getSupplierColumn'
import { getTextColumn, TextColumnConfig } from './definitions/getTextColumn'

export type ColumnConfig<T extends GridValidRowModel> =
  | TextColumnConfig<T>
  | CodeColumnConfig<T>
  | CountryColumnConfig<T>
  | BooleanColumnConfig<T>
  | ActionsColumnConfig<T>
  | ChipSingleColumnConfig<T>
  | ChipMultipleColumnConfig<T>
  | CustomEditableColumnConfig<T>
  | CustomRenderColumnConfig<T>
  | DateColumnConfig<T>
  | HyperlinkColumnConfig<T>
  | NumberColumnConfig<T>
  | AssigneesColumnConfig<T>
  | SupplierColumnConfig<T>
  | AgreementColumnConfig<T>
  | CheckboxColumnConfig<T>
  | PriceColumnConfig<T>

type Input<T extends GridValidRowModel> = {
  hasSupplierColumn: boolean
  columnConfig: ColumnConfig<T>[]
}

export const useColumnConfig = <T extends GridValidRowModel>({ columnConfig, hasSupplierColumn }: Input<T>): GridColDef<T>[] => {
  const { t: tCommon } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()
  const { push } = useRouter()

  return columnConfig
    .map((config) => {
      switch (config.columnType) {
        case 'text':
          return getTextColumn(config)
        case 'code':
          return getCodeColumn(config)
        case 'country':
          return getCountryColumn(config)
        case 'boolean':
          return getBooleanColumn(config)
        case 'actions':
          return getActionsColumn(config)
        case 'chip-single':
          return getChipColumn(config)
        case 'chip-multiple':
          return getMultiChipColumn(config)
        case 'custom-editable':
          return getCustomEditableRenderColumn(config)
        case 'custom-render':
          return getCustomRenderColumn(config)
        case 'date':
          return getDateColumn(config)
        case 'hyperlink':
          return getLinkColumn(config)
        case 'number':
          return getNumberColumn(config)
        case 'checkbox':
          return getCheckboxColumn(config)
        case 'price':
          return getPriceColumn(config)
        case 'supplier':
          if (!hasSupplierColumn) return null
          return getSupplierColumn({
            ...config,
            dependencies: { push },
          })
        case 'agreement':
          return getAgreementColumn(config)
        case 'assignees':
          return getAssigneesColumn({ ...config, dependencies: { tCommon, enqueueSnackbar } })
        default:
          assertNever(config)
          return null
      }
    })
    .filter((column): column is GridColDef<T> => column !== null)
}
